import React, { useEffect, useState } from 'react';
import intl from '$intl';
import { IonContent } from '@ionic/react';

import { Col, Div, Row, SPACE } from '$gstyles';
import { Button } from '$gcomponents/primitives';
import OrderModel from '$fbusiness/models/order';
import Footer from '$gcomponents/widgets/footer';
import OrderItemList from './orderItemList';

import InvoiceModel, { recalculateInvoice } from '$fbusiness/models/invoice';
import { ORDER_STATUS } from '$fbusiness/enums/options/orderStatus';
import { OrderSummary } from '$fcomponents';
import CurrentStateModel from '$fbusiness/models/currentState';
import { deriveItemPrice, derivePrice, initialPrice } from '$fbusiness/models/price';
import { recalculateItemPrice } from '$fbusiness/models/orderInvoiceItem';
import NativeSelect from '$gcomponents/primitives/selectInput/nativeSelect';
import { deriveShippingDistribution } from '$components/warehouse/utils';
import FactoryModel from '$fbusiness/models/factory';
import { getAccess, isEnabled, trimQty } from '$fbusiness/helpers/util';
import ItemPicker from '../../editInvoiceScreen/itemPicker';
import { getEl } from '$gbusiness/helpers/input';
import { deriveRawToItem } from '$fbusiness/models/item';
import { sleep } from '$gbusiness/helpers/util';
import ScanBackdrop from '../../editInvoiceScreen/scanBackdrop';

interface OrderShippingDetailsProps {
  order?: OrderModel;
  invoice?: InvoiceModel;
  onSubmitOrder: Function;
  currentState: CurrentStateModel;
  factory: FactoryModel;
  onClose: () => void;
}

const OrderShippingDetails: React.FC<OrderShippingDetailsProps> = ({
  order,
  factory,
  invoice,
  onClose,
  currentState,
  onSubmitOrder,
}) => {
  const [formItems, setFormItems] = useState<any>([]);
  const [pricing, setPricing] = useState<any>(initialPrice);
  const [notes, setNotes] = useState('');
  const [termId, setTermId] = useState(0);
  const [shippingMethodId, setShippingMethodId] = useState(0);

  const [qtyAdded, setQtyAdded] = useState(0);
  const [barcode, setBarcode] = useState('');
  const [showScan, setShowScan] = useState(false);
  const [scanItem, setScanItem] = useState<any>(undefined);
  const ACCESS = getAccess(currentState);

  const { settings } = factory;
  const warehouses = settings?.inventory ? factory?.warehouses || [] : [];
  const terms = factory?.terms || [];
  const shippingMethods = factory?.shippingMethods || [];
  const isOrderOos = isEnabled(settings, 'orderOos');

  useEffect(() => {
    if (!order?.termId) return;
    setTermId(order?.termId || 0);
  }, [order?.termId]);

  const initializeFormItems = (items, isInvoice = false) => {
    // if (invoice && !isInvoice) return;
    setFormItems(
      items.map((i) => {
        const status = invoice?.order?.status;
        const isFlagged = status === ORDER_STATUS.FLAGGED || status === ORDER_STATUS.CLOSED;
        const newQty = isFlagged ? 0 : i.qtyOrdered;
        const totalSent = parseInt(newQty || 0) + parseInt(i.totalSent || 0);
        const qty = (i.totalReceived || 0) + parseInt(newQty || 0);
        const defaultWarehouseId = i.warehouseId
          ? i.warehouseId
          : order?.warehouseId || warehouses[0]?.id || 0;

        return {
          ...i,
          ...deriveItemPrice({ ...i, qty }),
          qty: i.qty || 0,
          qtySent: i.distributions ? newQty : '',
          qtyOrdered: i.qtyOrdered || 0,
          ...(isInvoice && {
            qtySent: newQty || 0,
            originalSent: i.totalSent || 0,
            originalReceived: i.totalReceived,
            totalReceived: i.totalReceived || 0,
            totalSent,
          }),
          distributions: deriveShippingDistribution(i, warehouses, defaultWarehouseId, newQty || 0),
        };
      }),
    );
    setPricing(derivePrice(invoice || order));
  };
  useEffect(() => {
    if (!invoice) return;
    initializeFormItems(invoice.items, true);
    setNotes('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice]);

  useEffect(() => {
    if (!order) return;
    if (formItems.length && invoice) return;
    initializeFormItems(order.items);
    setNotes('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  useEffect(() => {
    const newInvoice = {
      ...recalculateInvoice(
        {
          ...(invoice || order),
          items: formItems || [],
        },
        { fixedItems: formItems },
      ),
    };
    setPricing(derivePrice(newInvoice));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formItems]);

  if (!order) return null;
  const { status } = order;

  const verifyDistribution = () => {
    if (!formItems.length) return;
    const filtered = formItems.reduce((acc, item) => {
      const errored = item.distributions.filter((d) => {
        return d.quantity > d.maxQty;
      });
      if (!errored.length) return acc;
      return [...acc, ...errored];
    }, []);
    if (filtered.length) {
      return filtered;
    }
    return null;
  };

  const onChangeQty = ({ newQty, newSubtotal, index, j = null, jValue = 0, blur }) => {
    setFormItems(
      formItems.map((item, i) => {
        if (i !== index) return item;
        if (newSubtotal !== undefined) {
          return recalculateItemPrice({ order: { ...item }, fixedSubtotal: newSubtotal });
        }
        const qtyDecimal = item.settings?.qtyDecimal ? 1 : 0;

        const totalSent = qtyDecimal
          ? trimQty(newQty, 3) + trimQty(item.originalSent, 3)
          : parseInt(newQty || 0) + parseInt(item.originalSent || 0);
        const qty = qtyDecimal
          ? item.totalReceived + trimQty(newQty, 2)
          : item.totalReceived + parseInt(newQty || 0);

        const newInvoice = recalculateItemPrice({ order: { ...item, qty } });
        return {
          ...newInvoice,
          qtySent: newQty,
          totalSent,
          ...(j !== null && {
            distributions: item.distributions.map((d, k) =>
              k === j ? { ...d, quantity: Number(jValue) || (blur ? '0' : '') } : d,
            ),
          }),
        };
      }),
    );
  };
  const onChangeNotes = (e) => {
    setNotes(e.target.value);
  };

  const onResetInput = async () => {
    focusOnInput();
  };
  const focusOnInput = async () => {
    const el = getEl('.react-autosuggest__input');
    el.focus();
  };
  const onScanMode = async () => {
    await onResetInput();
    setShowScan(true);
    setScanItem(undefined);
  };
  const onScanFetch = async (result, barcode) => {
    setBarcode(barcode);
    if (!result?.data) {
      setScanItem(null);
    } else {
      const item = deriveRawToItem({ ...result.data, scanned: true });
      const qty = result.qty || undefined;

      const itemIndex = formItems.findIndex((i) => i.itemId === item.id);
      const selectedItem = formItems[itemIndex];

      if (!selectedItem) {
        setScanItem(null);
        await sleep(2000);
        setScanItem(undefined);
        return;
      }

      setScanItem(item);
      setQtyAdded(qty);

      const newQty = qty === undefined ? (formItems[itemIndex]?.qty || 0) + (qty || 1) : qty;

      onChangeQty({
        newQty,
        newSubtotal: undefined,
        index: itemIndex,
        blur: true,
      });
    }
    await sleep(2000);
    setScanItem(undefined);
  };

  const onSubmit = () => {
    onSubmitOrder({ items: formItems, pricing, termId, shippingMethodId, noteFactory: notes });
  };

  const distributionErrors = verifyDistribution() && !isOrderOos;

  const thereAreUntouchedItem = !!formItems.find((i) => i.qtySent === '') || !!distributionErrors;

  return (
    <>
      <IonContent>
        <Div className="content-wrapper">
          <OrderItemList
            ACCESS={ACCESS}
            items={formItems}
            onChangeQty={onChangeQty}
            isInvoice={!!invoice}
            isRegenerated={status === ORDER_STATUS.FLAGGED}
          />
          <Row>
            <Col gridSize={6}>
              <Div padding={SPACE.LARGE} className="select-input">
                <NativeSelect
                  value={termId}
                  label="INPUT.LABEL.PAYMENT_TERM"
                  color="primary"
                  className="term-select"
                  onChange={setTermId}>
                  {[{ id: 0, name: 'None' }, ...terms].map((t, i) => (
                    <option key={i} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </NativeSelect>
              </Div>
              <Div padding={SPACE.LARGE} className="select-input">
                <NativeSelect
                  value={shippingMethodId}
                  label="INPUT.LABEL.SHIPPING_METHOD"
                  color="primary"
                  className="shipping-method-select"
                  onChange={setShippingMethodId}>
                  {[{ id: 0, name: 'None' }, ...shippingMethods].map((t, i) => (
                    <option key={i} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </NativeSelect>
              </Div>
            </Col>
            <Col gridSize={6}>
              <OrderSummary isHidden={currentState.hidePrice} price={pricing} />
            </Col>
          </Row>

          <Div padding={SPACE.LARGE}>
            {status === ORDER_STATUS.FLAGGED && invoice?.noteStore && (
              <div className="flag-message no-print">
                <div>{intl('SCREEN.SHIPPING_MODAL.NOTE_FROM_STORE')}</div>
                <div className="error">{invoice?.noteStore}</div>
              </div>
            )}
            <textarea
              onInput={onChangeNotes}
              className="notes"
              placeholder={intl('INPUT.PLACEHOLDER.NOTES')}
            />
          </Div>
        </Div>
      </IonContent>
      <Footer>
        <Button onClick={onClose} variant="outlined">
          {intl('BUTTON.CANCEL')}
        </Button>
        <ItemPicker
          scanMode={showScan}
          scanOnlyMode
          onScanFetch={onScanFetch}
          onSelectItem={() => {}}
          onClickScan={onScanMode}
          disabled={false}
          storeId={order?.storeId || 0}
        />
        <Button disabled={thereAreUntouchedItem} onClick={onSubmit}>
          {intl('SCREEN.SHIPPING_MODAL.SUBMIT_BUTTON')}
        </Button>
      </Footer>
      <ScanBackdrop
        qtyAdded={qtyAdded}
        item={scanItem}
        qtyMode={false}
        barcode={barcode}
        show={showScan}
        onClose={() => setShowScan(false)}
      />
    </>
  );
};

export default OrderShippingDetails;
